// WPS-009 + WPS-010 (admin) · Asset Library tab
// Admin browses + uploads assets (icons, photos, backgrounds, patterns).
import React, { useEffect, useState, useRef } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Upload, Image as ImageIcon, Trash2, AlertTriangle } from 'lucide-react';
import { assetsApi } from '../../services/api';

const fetchCategories = async () => {
  const base = (process.env.REACT_APP_BACKEND_URL || 'https://print.wunli.com').replace(/\/$/, '') + '/api';
  return fetch(`${base}/categories`).then(r => r.json()).catch(() => []);
};

const AssetLibrary = () => {
  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState({ type: 'all', category: 'all' });
  const [uploadType, setUploadType] = useState('photo');
  const [uploadCategory, setUploadCategory] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadWarning, setUploadWarning] = useState(null);
  const fileRef = useRef(null);

  const reload = async () => {
    const [a, c] = await Promise.all([assetsApi.adminList(), fetchCategories()]);
    setAssets(a || []);
    setCategories(c || []);
  };

  useEffect(() => { reload(); }, []);

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    setUploadWarning(null);
    const result = await assetsApi.upload(f, uploadType, uploadCategory || null);
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
    if (result?.success) {
      if (result.data?.warning) setUploadWarning(result.data.warning);
      await reload();
    } else {
      alert('Upload failed');
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete asset "${a.name}"?`)) return;
    await assetsApi.remove(a.id);
    await reload();
  };

  const filtered = assets.filter(a => {
    if (filter.type !== 'all' && a.type !== filter.type) return false;
    if (filter.category !== 'all' && a.categorySlug !== filter.category) return false;
    return true;
  });

  const byType = filtered.reduce((acc, a) => {
    const k = a.type || 'other';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle>Asset Library</CardTitle>
            <p className="text-sm text-gray-600 mt-1">
              {assets.length} total · {byType.icon || 0} icons · {byType.photo || 0} photos · {byType.background || 0} backgrounds
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={filter.type}
              onChange={(e) => setFilter({ ...filter, type: e.target.value })}
              className="border border-gray-300 px-2 py-1 text-sm font-mono"
            >
              <option value="all">All types</option>
              <option value="icon">Icons</option>
              <option value="photo">Photos</option>
              <option value="background">Backgrounds</option>
              <option value="pattern">Patterns</option>
            </select>
            <select
              value={filter.category}
              onChange={(e) => setFilter({ ...filter, category: e.target.value })}
              className="border border-gray-300 px-2 py-1 text-sm font-mono"
            >
              <option value="all">All categories</option>
              <option value="">Universal</option>
              {categories.map(c => (
                <option key={c.slug} value={c.slug}>{c.icon} {c.name}</option>
              ))}
            </select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">

        {/* Upload form */}
        <div className="border border-dashed border-gray-300 p-4 bg-gray-50">
          <div className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-3">Upload new asset</div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs">Type</Label>
              <select
                value={uploadType}
                onChange={(e) => setUploadType(e.target.value)}
                className="border border-gray-300 px-2 py-1 text-sm font-mono block"
              >
                <option value="photo">Photo</option>
                <option value="icon">Icon (raster)</option>
                <option value="background">Background</option>
                <option value="pattern">Pattern</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Category (optional)</Label>
              <select
                value={uploadCategory}
                onChange={(e) => setUploadCategory(e.target.value)}
                className="border border-gray-300 px-2 py-1 text-sm font-mono block"
              >
                <option value="">Universal</option>
                {categories.map(c => (
                  <option key={c.slug} value={c.slug}>{c.icon} {c.name}</option>
                ))}
              </select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs">File (PNG/JPG · 300 DPI recommended)</Label>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={handleUpload}
                disabled={uploading}
                className="text-sm block w-full mt-1"
              />
            </div>
            {uploading && <div className="text-sm text-blue-600 font-mono">Uploading…</div>}
          </div>
          {uploadWarning && (
            <div className="mt-3 text-xs font-mono text-amber-700 flex items-start gap-2 bg-amber-50 p-2 border-l-2 border-amber-500">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{uploadWarning}</span>
            </div>
          )}
        </div>

        {/* Gallery */}
        <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-8 gap-3">
          {filtered.map(a => (
            <div key={a.id} className="border border-gray-200 p-2 hover:border-blue-400 hover:shadow-sm transition-all relative group">
              <div className="aspect-square bg-gray-50 flex items-center justify-center mb-2 overflow-hidden">
                {a.rgbPath?.startsWith('lucide:') ? (
                  <div className="text-3xl">⬡</div>
                ) : a.rgbPath ? (
                  <img
                    src={a.rgbPath.startsWith('http') ? a.rgbPath : `${(process.env.REACT_APP_BACKEND_URL || 'https://print.wunli.com').replace(/\/$/, '')}${a.rgbPath}`}
                    alt={a.name}
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                ) : (
                  <ImageIcon className="h-8 w-8 text-gray-400" />
                )}
              </div>
              <div className="text-xs font-mono truncate" title={a.name}>{a.name}</div>
              <div className="text-[10px] text-gray-500 uppercase tracking-wider">
                {a.type} · {a.categorySlug || 'universal'}
              </div>
              <button
                onClick={() => remove(a)}
                className="absolute top-1 right-1 bg-white border border-gray-200 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Delete"
              >
                <Trash2 className="h-3 w-3 text-red-600" />
              </button>
            </div>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <ImageIcon className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p>No assets match the current filters.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AssetLibrary;
